import { useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, PlanningItem } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useBestaetigung, useFormularZustand } from "@kundeninfo/ui";
import type { Bestaetigungsfrage } from "@kundeninfo/ui";
import { Auswahl, Chip, Eingabe, Formular, Hinweis, Optionen, Schalter } from "@kundeninfo/ui/me-design";
import { errorProp, fieldErrorInsteadOf409 } from "../../components/fieldErrors.js";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import { formatBetrag, formatDatum } from "../../format.js";
import { AMOUNT_FIELD_MAX, DUE_DAY_MAX, amountForApi, cents, wholeNumber } from "../suppliers/shared.js";
import { euro } from "./liquidityModel.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import "./Liquidity.css";

const PI = T.planningItem;
/** `LABEL_MAX`, `PLANNING_NOTE_MAX` und `NOTE_MAX` der Domäne; web importiert sie nicht. */
const LABEL_MAX = 200, PLANNING_NOTE_MAX = 500, AMOUNT_NOTE_MAX = 200;
/** Der früheste Betrag bleibt: kein fremder Stand, sondern ein Hinweis (`fields` ohne Feld in der Maske landen in `meldung`). */
const AMOUNT_LAST: readonly string[] = ["PLANNING_AMOUNT_LAST"];
const HEAD_FIELDS: readonly string[] = ["label", "category", "firstDueDate", "dueDayOfMonth", "endDate", "note", "amount"];

type Kind = PlanningItem["kind"];
type Category = PlanningItem["category"];
type Cycle = PlanningItem["cycle"];
export type PlanningAmount = PlanningItem["amounts"][number];
export type PlanningItemMode = { kind: "create"; group: "IN" | "OUT"; companyId: string } | { kind: "edit"; item: PlanningItem };

const KINDS = Object.keys(PI.kinds) as Kind[];
const CATEGORIES = Object.keys(PI.categories) as Category[];
const CYCLES = Object.keys(PI.cycles) as Cycle[];

type Draft = { kind: Kind; category: Category | ""; label: string; cycle: Cycle; firstDue: string; dueDay: string; amount: string; end: string; scenarioOnly: boolean; note: string };

const draftOfGroup = (group: "IN" | "OUT"): Draft => ({ kind: group === "IN" ? "REVENUE" : "COST", category: "", label: "", cycle: "MONTHLY", firstDue: "", dueDay: "",
  amount: "", end: "", scenarioOnly: false, note: "" });
const draftOfItem = (i: PlanningItem): Draft => ({ kind: i.kind, category: i.category, label: i.label, cycle: i.cycle, firstDue: i.firstDueDate,
  dueDay: i.dueDayOfMonth === null ? "" : String(i.dueDayOfMonth), amount: "", end: i.endDate ?? "", scenarioOnly: i.scenarioId !== null, note: i.note ?? "" });

const dayOf = (date: string): string => (date === "" ? "" : String(Number(date.slice(8, 10))));
/** Der Zahltag folgt der ersten Zahlung, solange er ihr Tag ist oder leer: Vorschlag, kein Zwang. Ob er passt, entscheidet der Server. */
const withFirstDue = (d: Draft, date: string): Draft =>
  ({ ...d, firstDue: date, dueDay: d.dueDay === "" || d.dueDay === dayOf(d.firstDue) ? dayOf(date) : d.dueDay });

/** Ein Planbetrag, wie er gilt: ganze Euro wie überall in der Liquidität, ein Betrag mit Cent genau. */
function amountText(amount: string): string {
  const c = Number(cents(amount));
  return c % 100 === 0 ? euro(c) : formatBetrag(c / 100, "EUR");
}

/** Die Frage vor dem Löschen eines Betrags, im Panel und in der Spalte. */
export const deleteAmountQuestion = (a: PlanningAmount): Bestaetigungsfrage => ({ titel: PI.deleteAmountTitle, bestaetigenText: PI.deleteAmountConfirm, gefaehrlich: true,
  text: PI.deleteAmountText(amountText(a.amount), formatDatum(a.validFrom)) });

/** Eine 409 bringt den aktuellen Satz mit; „Neu laden“ übernimmt ihn, das Panel braucht dafür keinen Leseweg. */
async function noteCurrent<R>(run: () => Promise<R>, keep: (current: PlanningItem) => void): Promise<R> {
  try {
    return await run();
  } catch (e) {
    const current = e instanceof ApiError ? e.conflict?.current as PlanningItem | undefined : undefined;
    if (current !== undefined) keep(current);
    throw e;
  }
}

/**
 * Der Betrag und dahinter der Platz seines Knopfs (`action`, auch `false`: Platz ohne Knopf). Beide bleiben zusammen und stehen rechts, auch wenn die
 * Zeile umbricht: So stehen alle Beträge einer Liste auf einer Kante (Hausregel „Zahlen rechtsbündig“).
 */
export function AmountEnd(p: { amount: string; action?: ReactNode }): ReactElement {
  return (
    <span className="liq__amount-end">
      <span className="liq__amount">{p.amount}</span>
      {p.action !== undefined && <span className="liq__amount-action">{p.action}</span>}
    </span>
  );
}

/** Eine Zeile „ab wann, Bemerkung, Betrag“: die Stände eines Kontos und die Beträge einer Planposition. */
export function DatedAmountRow(p: { label: string; note: string | null; amount: string; amountId?: string; mark?: ReactNode; action?: ReactNode }): ReactElement {
  return (
    <li className="liq__post" data-amount-id={p.amountId}>
      <span className="liq__post-text">
        <span>{p.label}</span>
        {p.note !== null && <span className="liq__muted">{p.note}</span>}
      </span>
      {p.mark}
      <AmountEnd amount={p.amount} action={p.action} />
    </li>
  );
}

/**
 * DIE BETRÄGE AB `validFrom` (Spec „Planpositionen“, 558): kein Ende je Betrag, der Nachfolger beendet den Vorgänger, „0 €“ ist die Pause. Die
 * Liste steht nach `validFrom` aufsteigend (Server); der früheste gilt ab der ersten Zahlung und hat keinen Löschen-Knopf. Sein Platz bleibt
 * trotzdem frei, damit alle Beträge auf einer Kante stehen (Hausregel „Zahlen rechtsbündig“).
 */
export function PlanningAmountList(p: { amounts: readonly PlanningAmount[]; locked: boolean; onDelete: (a: PlanningAmount) => void }): ReactElement {
  return (
    <ul className="liq__posts">
      {p.amounts.map((a, k) => (
        <DatedAmountRow key={a.id} amountId={a.id} label={PI.amountFrom(formatDatum(a.validFrom))} note={a.note} amount={amountText(a.amount)}
          mark={Number(a.amount) === 0 && <Chip>{PI.pause}</Chip>}
          action={k > 0 && <LoeschKnopf name={PI.deleteAmountOf(formatDatum(a.validFrom))} deaktiviert={p.locked} onClick={() => p.onDelete(a)} />} />
      ))}
    </ul>
  );
}

/**
 * BETRAG ÄNDERN AB … (Spec „Dialoge › Planposition“, P13): Datum, Betrag (≥ 0, 0 = Pause), Bemerkung. Neben dem Panel der Planposition steht
 * es seitlich, aus der Spalte für sich. Trägt die Version der Planposition; „Speichern“ ist die Bestätigung, nur ein Betrag am selben Tag fragt
 * gestapelt, denn er ersetzt den vorhandenen (H13). Ob der Tag zur Planposition passt, entscheidet der Server: Sein Befund steht am Feld.
 */
export function PlanningAmountPanel(p: {
  item: PlanningItem; client: Pick<ApiClient, "addPlanningItemAmount">; onSaved: (item: PlanningItem) => void; onCancel: () => void;
}): ReactElement {
  const [item, setItem] = useState(p.item);
  const [draft, setDraft] = useState({ date: "", amount: "", note: "" });
  const [tried, setTried] = useState(false);
  const { bestaetigen } = useBestaetigung();
  const latest = useRef<PlanningItem | null>(null);
  const amount = amountForApi(draft.amount), note = draft.note.trim();
  const form = useFormularZustand<PlanningItem>({
    speichern: () => noteCurrent(() => p.client.addPlanningItemAmount(item.id, { version: item.version, validFrom: draft.date, amount: amount!, note: note === "" ? null : note }),
      (current) => { latest.current = current; }),
    // Ein neuer Betrag ist keine Kopie des Serverstands: Die Eingaben bleiben, Version und vorhandene Beträge rücken nach.
    neuLaden: async () => { if (latest.current !== null) setItem(latest.current); },
    bekannteFelder: ["validFrom", "amount", "note"],
  });
  const dirty = draft.date !== "" || draft.amount !== "" || draft.note !== "";
  useBeforeUnload(dirty);

  async function save(): Promise<void> {
    setTried(true);
    if (amount === null || draft.date === "") return;
    const same = item.amounts.find((a) => a.validFrom === draft.date);
    if (same !== undefined && !(await bestaetigen({ titel: PI.replaceTitle, bestaetigenText: PI.replaceConfirm,
      text: PI.replaces(amountText(same.amount), formatDatum(same.validFrom)) }))) return;
    const answer = await form.aktion();
    if (answer !== undefined) p.onSaved(answer);
  }
  async function leave(): Promise<void> {
    if (!form.laeuft && await darfAbbrechen(dirty)) p.onCancel();
  }

  return (
    <Modal titel={PI.changeAmount} onAbbrechen={() => { void leave(); }} abbrechenGesperrt={form.laeuft}>
      {form.meldung !== null && <Hinweis art="fehler">{form.meldung}</Hinweis>}
      <Formular beschriftung="oben" {...form.formularProps}
        hinweis={form.zustand === "fremder-stand" ? <span>{T.dialogs.changedElsewhere}</span> : undefined}>
        <div className="liq__mask">
          <p className="liq__muted">{item.label}</p>
          <Eingabe art="datum" beschriftung={PI.amountDate} wert={draft.date} pflicht
            {...errorProp(form.feldFehler.validFrom ?? (tried && draft.date === "" ? T.dialogs.dateRequired : undefined))}
            onGeaendert={(e) => setDraft((d) => ({ ...d, date: e.wert }))} />
          <Eingabe art="dezimal" beschriftung={PI.amountValue} einheit={T.dialogs.euroUnit} wert={draft.amount} pflicht maxLength={AMOUNT_FIELD_MAX}
            {...errorProp(form.feldFehler.amount ?? (tried && amount === null ? T.dialogs.amountInvalid : undefined))}
            onGeaendert={(e) => setDraft((d) => ({ ...d, amount: e.wert }))} />
          <Eingabe beschriftung={PI.amountNote} mehrzeilig wert={draft.note} maxLength={AMOUNT_NOTE_MAX} {...errorProp(form.feldFehler.note)}
            onGeaendert={(e) => setDraft((d) => ({ ...d, note: e.wert }))} />
        </div>
      </Formular>
      <ModalFuss speichernText={T.dialogs.save} speichernGesperrt={form.zustand !== "bereit"} abbrechenGesperrt={form.laeuft}
        onSpeichern={() => { void save(); }} onAbbrechen={() => { void leave(); }} />
    </Modal>
  );
}

/**
 * PLANPOSITION ANLEGEN ODER ÄNDERN (Spec „Dialoge › Planposition“, 558, 600): Modalpanel rechts, „Speichern“ ist die Bestätigung. Kategorie nur
 * bei Ausgabe (Einnahme speichert `OTHER`, Ruling 6), Zahltag und Ende nur wiederkehrend; `null` bei „Einmalig“. „nur im Szenario“ steht nur mit
 * gewähltem Szenario da, ohne eines bleibt die gespeicherte Zuordnung. Die Fachregeln entscheidet der Server, seine Befunde stehen am Feld.
 *
 * Beim Ändern stehen die Beträge darunter. Ihre Wege drehen die Version und bleiben im Panel, die Eingaben am Kopf bleiben dabei stehen:
 * `onAmountsChanged` lädt dahinter die Reihe neu, die Quittung kommt mit dem Schließen. Alle Schreibwege laufen über denselben Formularzustand,
 * damit ein fremder Stand überall gleich aussieht.
 */
export function PlanningItemPanel(p: {
  mode: PlanningItemMode;
  scenario: { id: string; name: string } | null;
  client: Pick<ApiClient, "createPlanningItem" | "updatePlanningItem" | "deletePlanningItem" | "addPlanningItemAmount" | "deletePlanningItemAmount">;
  /** Der Name des Unternehmens der Planposition, wenn die Sicht mehrere zeigt. */
  companyName?: string | null;
  /** Der USt-Hinweis der Reihe (Spec „USt-Hinweis“): steht bei einer Ausgabe der Kategorie `TAX` im Panel. */
  vatHint?: string | null;
  /** Das Panel ist fertig, gespeichert oder gelöscht; der Satz ist die Quittung. */
  onSaved: (receipt: string) => void;
  /** Ein Betrag ist gespeichert oder gelöscht, das Panel bleibt offen. */
  onAmountsChanged: (receipt: string) => void;
  onCancel: () => void;
}): ReactElement {
  const { mode, client } = p, companyName = p.companyName ?? null, vatHint = p.vatHint ?? null;
  const [item, setItem] = useState(mode.kind === "edit" ? mode.item : null);
  const [start, setStart] = useState(() => (mode.kind === "edit" ? draftOfItem(mode.item) : draftOfGroup(mode.group)));
  const [draft, setDraft] = useState(start);
  const [tried, setTried] = useState(false);
  const [amountOpen, setAmountOpen] = useState(false);
  const { bestaetigen } = useBestaetigung();
  /** Der laufende Schreibweg; seine Antwort sagt, was danach geschieht. */
  const write = useRef<() => Promise<() => void>>(() => Promise.resolve(() => {}));
  const latest = useRef<PlanningItem | null>(null);
  const form = useFormularZustand<() => void>({
    speichern: () => noteCurrent(() => fieldErrorInsteadOf409(write.current, AMOUNT_LAST), (current) => { latest.current = current; }),
    neuLaden: async () => {
      if (latest.current === null) return;
      const d = draftOfItem(latest.current);
      setItem(latest.current);
      setStart(d);
      setDraft(d);
      setTried(false);
    },
    bekannteFelder: HEAD_FIELDS,
  });
  const dirty = JSON.stringify(draft) !== JSON.stringify(start);
  useBeforeUnload(dirty);
  const top = useRef<HTMLDivElement>(null);
  // Die Beträge stehen unten im Panel: Ein Befund oder fremder Stand stünde oben sonst außerhalb des Blicks.
  useEffect(() => {
    if (form.meldung !== null || form.zustand === "fremder-stand") top.current?.scrollIntoView?.({ block: "nearest" });
  }, [form.meldung, form.zustand]);

  const recurring = draft.cycle !== "ONCE";
  const dueDay = wholeNumber(draft.dueDay, 1, DUE_DAY_MAX), amount = amountForApi(draft.amount), label = draft.label.trim(), note = draft.note.trim();
  const own: Readonly<Record<string, string | undefined>> = {
    label: label === "" ? PI.labelRequired : undefined,
    category: draft.kind === "COST" && draft.category === "" ? PI.categoryRequired : undefined,
    firstDueDate: draft.firstDue === "" ? T.dialogs.dateRequired : undefined,
    dueDayOfMonth: recurring && typeof dueDay !== "number" ? PI.dueDayInvalid : undefined,
    amount: item === null && amount === null ? T.dialogs.amountInvalid : undefined,
  };
  const at = (field: string): { fehler?: string } => errorProp(form.feldFehler[field] ?? (tried ? own[field] : undefined));
  const set = (change: Partial<Draft>): void => setDraft((d) => ({ ...d, ...change }));
  const locked = form.zustand !== "bereit";

  async function run(op: () => Promise<() => void>): Promise<void> {
    write.current = op;
    (await form.aktion())?.();
  }
  async function save(): Promise<void> {
    setTried(true);
    if (Object.values(own).some((e) => e !== undefined)) return;
    const head = { kind: draft.kind, category: draft.kind === "REVENUE" ? "OTHER" as const : draft.category as Category, label, cycle: draft.cycle, firstDueDate: draft.firstDue,
      dueDayOfMonth: recurring ? dueDay as number : null, endDate: recurring && draft.end !== "" ? draft.end : null, note: note === "" ? null : note,
      scenarioId: p.scenario === null ? item?.scenarioId ?? null : draft.scenarioOnly ? item?.scenarioId ?? p.scenario.id : null };
    await run(async () => {
      if (item !== null) await client.updatePlanningItem(item.id, { version: item.version, ...head });
      else if (mode.kind === "create") await client.createPlanningItem({ companyId: mode.companyId, ...head, amount: amount! });
      return () => p.onSaved(PI.saved);
    });
  }
  async function deleteItem(i: PlanningItem): Promise<void> {
    if (!(await bestaetigen({ titel: PI.deleteTitle, bestaetigenText: PI.deleteConfirm, gefaehrlich: true, text: PI.deleteText(i.label) }))) return;
    await run(async () => {
      await client.deletePlanningItem(i.id, i.version);
      return () => p.onSaved(PI.deleted);
    });
  }
  async function deleteAmount(i: PlanningItem, a: PlanningAmount): Promise<void> {
    if (!(await bestaetigen(deleteAmountQuestion(a)))) return;
    await run(async () => {
      const next = await client.deletePlanningItemAmount(i.id, a.id, i.version);
      return () => {
        setItem(next);
        p.onAmountsChanged(PI.amountDeleted);
      };
    });
  }
  async function leave(): Promise<void> {
    if (!form.laeuft && await darfAbbrechen(dirty)) p.onCancel();
  }

  return (
    <>
      <Modal titel={item === null ? PI.titleNew : PI.titleEdit} onAbbrechen={() => { void leave(); }} abbrechenGesperrt={form.laeuft}>
        <div ref={top} />
        {form.meldung !== null && <Hinweis art="fehler">{form.meldung}</Hinweis>}
        <div className="liq__mask">
          <Formular beschriftung="oben" {...form.formularProps}
            hinweis={form.zustand === "fremder-stand" ? <span>{T.dialogs.changedElsewhere}</span> : undefined}>
            <div className="liq__mask">
              {companyName !== null && <Eingabe beschriftung={T.dialogs.account.company} wert={companyName} gesperrt />}
              <Optionen beschriftung={PI.kind} darstellung="segmente" richtung="waagerecht" wert={draft.kind}
                onGeaendert={(d) => { const kind = KINDS.find((k) => k === d.wert); if (kind !== undefined) set({ kind }); }}>
                {KINDS.map((k) => <option key={k} value={k}>{PI.kinds[k]}</option>)}
              </Optionen>
              {draft.kind === "COST" && (
                <Auswahl beschriftung={PI.category} wert={draft.category} {...at("category")} onGeaendert={(e) => set({ category: e.wert as Draft["category"] })}>
                  <option value="">{PI.chooseCategory}</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{PI.categories[c]}</option>)}
                </Auswahl>
              )}
              {draft.kind === "COST" && draft.category === "TAX" && vatHint !== null && <Hinweis art="info">{vatHint}</Hinweis>}
              <Eingabe beschriftung={PI.label} wert={draft.label} pflicht maxLength={LABEL_MAX} {...at("label")} onGeaendert={(e) => set({ label: e.wert })} />
              <Auswahl beschriftung={PI.cycle} wert={draft.cycle} onGeaendert={(e) => setDraft((d) => ({ ...d, cycle: e.wert as Cycle, dueDay: d.dueDay === "" ? dayOf(d.firstDue) : d.dueDay }))}>
                {CYCLES.map((c) => <option key={c} value={c}>{PI.cycles[c]}</option>)}
              </Auswahl>
              <Eingabe art="datum" beschriftung={PI.firstDue} wert={draft.firstDue} pflicht {...at("firstDueDate")}
                onGeaendert={(e) => setDraft((d) => withFirstDue(d, e.wert))} />
              {recurring && (
                <Eingabe art="zahl" beschriftung={PI.dueDay} wert={draft.dueDay} pflicht hilfe={PI.dueDayHint} {...at("dueDayOfMonth")}
                  onGeaendert={(e) => set({ dueDay: e.wert })} />
              )}
              {item === null && (
                <Eingabe art="dezimal" beschriftung={PI.amount} einheit={T.dialogs.euroUnit} wert={draft.amount} pflicht maxLength={AMOUNT_FIELD_MAX} {...at("amount")}
                  onGeaendert={(e) => set({ amount: e.wert })} />
              )}
              {recurring && (
                <Eingabe art="datum" beschriftung={PI.end} wert={draft.end} hilfe={PI.endHint} {...at("endDate")} onGeaendert={(e) => set({ end: e.wert })} />
              )}
              {p.scenario !== null && (
                <Schalter beschriftung={PI.scenarioOnly(p.scenario.name)} an={draft.scenarioOnly} onGeaendert={(e) => set({ scenarioOnly: e.an })} />
              )}
              <Eingabe beschriftung={PI.note} mehrzeilig wert={draft.note} maxLength={PLANNING_NOTE_MAX} {...at("note")} onGeaendert={(e) => set({ note: e.wert })} />
            </div>
          </Formular>
          {item !== null && (
            <>
              <section className="liq__mask">
                <h3 className="liq__history-title">{PI.amounts}</h3>
                <PlanningAmountList amounts={item.amounts} locked={locked} onDelete={(a) => { void deleteAmount(item, a); }} />
                <KiKnopf kompakt className="liq__reset" deaktiviert={locked} onClick={() => setAmountOpen(true)}>{PI.changeAmount}</KiKnopf>
              </section>
              <KiKnopf art="gefahr" kompakt className="liq__reset" deaktiviert={locked} onClick={() => { void deleteItem(item); }}>{PI.delete}</KiKnopf>
            </>
          )}
        </div>
        <ModalFuss speichernText={T.dialogs.save} speichernGesperrt={locked || amountOpen} abbrechenGesperrt={form.laeuft || amountOpen}
          onSpeichern={() => { void save(); }} onAbbrechen={() => { void leave(); }} />
      </Modal>
      {/* Seitlich daneben (Hausregel „Sub-Inhalte seitlich“), als Geschwister des Panels wie das Adresspanel der Rechnungsangaben. */}
      {amountOpen && item !== null && (
        <PlanningAmountPanel item={item} client={client} onCancel={() => setAmountOpen(false)}
          onSaved={(next) => {
            // „Neu laden“ im zweiten Panel kann einen fremden Kopf gebracht haben, die Eingaben hier stehen noch auf dem alten (`start`). Dann bleibt
            // die alte Version: Speichern läuft in den Konflikt, statt den fremden Kopf still zu überschreiben.
            setItem(JSON.stringify(draftOfItem(next)) === JSON.stringify(start) ? next : { ...next, version: item.version });
            setAmountOpen(false);
            p.onAmountsChanged(PI.amountSaved);
          }} />
      )}
    </>
  );
}
